import type { Category, Document, Workspace } from "@elaso/shared/types/db";
import SuperJSON from "superjson";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { ChannelEntry } from "./core-store";

/**
 * Standalone, persisted client-side cache (D2 / TECH-2944), a leaf store that
 * imports no other store. It holds the render-only subset (ids, names, ordering,
 * unread markers) that lets `/app` paint instantly on reload before a live
 * connection exists. All hydration and write-through *logic* lives in the stores
 * that import THIS one (`core-store`, `update-state-handler`, `auth-store`),
 * never the reverse, which keeps the leaf boundary cycle-free.
 *
 * No permission data is ever persisted
 * (docs/adr/21-what-happens-when-the-learner-client-first-connects.md). The
 * `Cached*` types are explicit allowlists with `permissions` structurally
 * absent, so a future authz field added upstream cannot leak into localStorage
 * by default. Channels are cached as the *accessible subset* only (the filtering
 * happens in `core-store` before `setChannels`), so "present in the cache" means
 * "was accessible at last load", not a permission flag.
 */

// Bump whenever a Cached* shape changes. On mismatch the cache is dropped and
// refetched, because it is always re-derivable from the chain. No migrations.
export const DATA_CACHE_VERSION = 2;

export type CachedWorkspace = Pick<Workspace, "id" | "name">;

// Categories are workspace-wide groupings, not access-filtered, so the full set
// is cached. No accessible-subset handling, unlike channels.
export type CachedCategory = Pick<
	Category,
	"id" | "name" | "workspaceId" | "order" | "isVisible" | "maxParticipants"
>;

export type CachedChannel = Pick<
	ChannelEntry,
	| "id"
	| "name"
	| "type"
	| "workspaceId"
	| "parentId"
	| "isThread"
	| "closedAt"
	| "categoryId"
	| "order"
	| "openOnFirstLogin"
	| "description"
	| "readStatus"
	| "latestMessageId"
	| "latestMessageAt"
	| "forcedUnreadMessageId"
	| "mentionCount"
	| "muteStatus"
>; // Deliberately no `permissions` and no `pagination`.

export type CachedDocument = Pick<
	Document,
	| "id"
	| "name"
	| "url"
	| "icon"
	| "isExternal"
	| "isActive"
	| "workspaceId"
	| "primaryRoleKey"
>;

type CachedDocumentSet = { docs: CachedDocument[]; complete: boolean };

// Explicit field copies (not `omit`) so new upstream fields stay out by default.

export function toCachedWorkspace(w: Workspace): CachedWorkspace {
	return { id: w.id, name: w.name };
}

export function toCachedCategory(c: Category): CachedCategory {
	return {
		id: c.id,
		name: c.name,
		workspaceId: c.workspaceId,
		order: c.order,
		isVisible: c.isVisible,
		maxParticipants: c.maxParticipants,
	};
}

export function toCachedChannel(c: ChannelEntry): CachedChannel {
	return {
		id: c.id,
		name: c.name,
		type: c.type,
		workspaceId: c.workspaceId,
		parentId: c.parentId,
		isThread: c.isThread,
		closedAt: c.closedAt,
		categoryId: c.categoryId,
		order: c.order,
		openOnFirstLogin: c.openOnFirstLogin,
		description: c.description,
		readStatus: c.readStatus,
		latestMessageId: c.latestMessageId,
		latestMessageAt: c.latestMessageAt,
		forcedUnreadMessageId: c.forcedUnreadMessageId,
		mentionCount: c.mentionCount,
		muteStatus: c.muteStatus,
	};
}

export function toCachedDocument(d: Document): CachedDocument {
	return {
		id: d.id,
		name: d.name,
		url: d.url,
		icon: d.icon,
		isExternal: d.isExternal,
		isActive: d.isActive,
		workspaceId: d.workspaceId,
		primaryRoleKey: d.primaryRoleKey,
	};
}

type DataCacheState = {
	ownerUid: string | null;
	workspaces: CachedWorkspace[];
	channelsByWorkspace: Record<string, CachedChannel[]>;
	categoriesByWorkspace: Record<string, CachedCategory[]>;
	documentsByWorkspace: Record<string, CachedDocumentSet>;
};

type DataCacheActions = {
	setOwnerUid: (uid: string | null) => void;
	setWorkspaces: (workspaces: CachedWorkspace[]) => void;
	setChannels: (workspaceId: string, channels: CachedChannel[]) => void;
	setCategories: (workspaceId: string, categories: CachedCategory[]) => void;
	setDocuments: (
		workspaceId: string,
		docs: CachedDocument[],
		complete: boolean
	) => void;
	/**
	 * Drop one workspace's cache entirely: its channels, categories, documents
	 * and its row in the picker (TECH-3259). Called when access to that workspace
	 * is revoked mid-session, so a reload cannot paint a channel list for a
	 * workspace the user no longer participates in.
	 */
	clearWorkspace: (workspaceId: string) => void;
	reset: () => void;
};

export type DataCacheStore = DataCacheState & DataCacheActions;

const getInitialState = (): DataCacheState => ({
	ownerUid: null,
	workspaces: [],
	channelsByWorkspace: {},
	categoriesByWorkspace: {},
	documentsByWorkspace: {},
});

// Persisted payload carries the version so a shape change drops the cache.
type PersistedDataCache = DataCacheState & { _cacheVersion: number };

const customStorage = {
	getItem: (name: string) => {
		const data = localStorage.getItem(name);
		if (!data) return null;
		try {
			const parsed = SuperJSON.parse(data) as {
				state: PersistedDataCache;
				version?: number;
			};
			// Disposable cache. Any version mismatch drops and refetches, no migration.
			if (parsed.state?._cacheVersion !== DATA_CACHE_VERSION) {
				localStorage.removeItem(name);
				return null;
			}
			return parsed;
		} catch {
			localStorage.removeItem(name);
			return null;
		}
	},
	setItem: (name: string, value: unknown) => {
		localStorage.setItem(name, SuperJSON.stringify(value));
	},
	removeItem: (name: string) => {
		localStorage.removeItem(name);
	},
} as const;

export const dataCacheStore = createStore<DataCacheStore>()(
	persist(
		immer((set) => ({
			...getInitialState(),
			setOwnerUid: (uid) => {
				set((state) => {
					state.ownerUid = uid;
				});
			},
			setWorkspaces: (workspaces) => {
				set((state) => {
					state.workspaces = workspaces;
				});
			},
			setChannels: (workspaceId, channels) => {
				set((state) => {
					state.channelsByWorkspace[workspaceId] = channels;
				});
			},
			setCategories: (workspaceId, categories) => {
				set((state) => {
					state.categoriesByWorkspace[workspaceId] = categories;
				});
			},
			setDocuments: (workspaceId, docs, complete) => {
				set((state) => {
					state.documentsByWorkspace[workspaceId] = { docs, complete };
				});
			},
			clearWorkspace: (workspaceId) => {
				set((state) => {
					delete state.channelsByWorkspace[workspaceId];
					delete state.categoriesByWorkspace[workspaceId];
					delete state.documentsByWorkspace[workspaceId];
					state.workspaces = state.workspaces.filter(
						(w) => w.id !== workspaceId
					);
				});
			},
			reset: () => {
				set(getInitialState());
			},
		})),
		{
			name: "elaso-data-cache",
			storage: customStorage as any,
			partialize: (state) => ({
				_cacheVersion: DATA_CACHE_VERSION,
				ownerUid: state.ownerUid,
				workspaces: state.workspaces,
				channelsByWorkspace: state.channelsByWorkspace,
				categoriesByWorkspace: state.categoriesByWorkspace,
				documentsByWorkspace: state.documentsByWorkspace,
			}),
		}
	)
);
